import React, { createContext, useContext, useState, useEffect } from 'react'
import { Layout, Spin, message } from 'antd'
import api from '../utils/api'

const AuthContext = createContext({})

const parsePermissions = (permissions) => {
  if (!permissions) return {}
  if (typeof permissions === 'object') return permissions
  if (typeof permissions === 'string') {
    try {
      return JSON.parse(permissions)
    } catch {
      return {}
    }
  }
  return {}
}

const normalizeUser = (rawUser) => {
  if (!rawUser) return null
  const roleDetails = typeof rawUser.role === 'object' && rawUser.role !== null
    ? rawUser.role
    : (rawUser.roleDetails || {})
  const roleCode = typeof rawUser.role === 'string'
    ? rawUser.role
    : (roleDetails.code || rawUser.role_code || '')
  const permissions = parsePermissions(rawUser.permissions || roleDetails.permissions)

  return {
    ...rawUser,
    role: roleCode,
    roleDetails: { ...roleDetails, code: roleCode, permissions },
    permissions
  }
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [token, setToken] = useState(localStorage.getItem('token'))
  const [accessMenus, setAccessMenus] = useState([])
  const [accessLoading, setAccessLoading] = useState(true)

  const refreshAccess = async () => {
    try {
      setAccessLoading(true)
      const response = await api.get('/api/access-control/effective')
      setAccessMenus(response.data.data?.menus || [])
      return response.data.data
    } catch (error) {
      setAccessMenus([])
      return null
    } finally {
      setAccessLoading(false)
    }
  }

  const isTokenExpired = (token) => {
    try {
      const decoded = JSON.parse(atob(token.split('.')[1]));
      return decoded.exp * 1000 < Date.now();
    } catch {
      return true;
    }
  };
  

  // Check if user is authenticated on app start
useEffect(() => {
  const checkAuth = async () => {
    const savedToken = localStorage.getItem('token');
    const savedRefreshToken = localStorage.getItem('refreshToken');

    if (!savedToken || !savedRefreshToken) {
      setLoading(false);
      return;
    }

    try {
      
      // ตั้งค่า header
      api.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;

      // ตรวจสอบ token
      const response = await api.get('/api/auth/me');
      //console.log('verifying...', response);

      if (response.data.success) {
        setUser(normalizeUser(response.data.data));
        setToken(savedToken);
        await refreshAccess();
      } else {
        throw new Error('Invalid token');
      }
    } catch (error) {
      // The API interceptor already attempted one refresh and cleared an invalid session.
      setUser(null);
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  checkAuth();
}, []);


   // Login function
  const login = async (credentials) => {
    try {
      setLoading(true)

      const response = await api.post('/api/auth/login', credentials)

      if (response.data.success) {
        const { data } = response.data
        const { token: newToken, refreshToken: newRefreshToken, ...userData } = data

        // Save token and user data
        localStorage.setItem('token', newToken)
        localStorage.setItem('refreshToken', newRefreshToken)
        setToken(newToken)
        setUser(normalizeUser(userData))

        // Set token in API headers
        api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`
        await refreshAccess()

        message.success('เข้าสู่ระบบสำเร็จ')

        return { success: true, user: userData }
      } else {
        message.error(response.data.message || 'เข้าสู่ระบบไม่สำเร็จ')
        return { success: false, message: response.data.message }
      }
    } catch (error) {
      console.error('Login error:', error)

      const errorMessage = error.response?.data?.message || 'เกิดข้อผิดพลาดในการเข้าสู่ระบบ'
      message.error(errorMessage)

      return { success: false, message: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  // Logout function
  const logout = () => {
    // Fire-and-forget: the audit call must use the current access token before
    // local credentials are cleared, but logout must remain instant for the UI.
    api.post('/api/auth/logout').catch(() => {})
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    setToken(null)
    setUser(null)
    setAccessMenus([])
    delete api.defaults.headers.common['Authorization']
    message.success('ออกจากระบบสำเร็จ')
  }

  // Update user profile
  const updateProfile = async (profileData) => {
    try {
      const response = await api.put(`/api/users/${user.id}`, profileData)

      if (response.data.success) {
        // Refresh user data
        const userResponse = await api.get('/api/auth/me')
        if (userResponse.data.success) {
          setUser(normalizeUser(userResponse.data.data))
        }

        message.success('ปรับปรุงข้อมูลสำเร็จ')
        return { success: true }
      } else {
        message.error(response.data.message || 'ปรับปรุงข้อมูลไม่สำเร็จ')
        return { success: false, message: response.data.message }
      }
    } catch (error) {
      console.error('Profile update error:', error)

      const errorMessage = error.response?.data?.message || 'เกิดข้อผิดพลาดในการปรับปรุงข้อมูล'
      message.error(errorMessage)

      return { success: false, message: errorMessage }
    }
  }

  // Change password
  const changePassword = async (passwordData) => {
    try {
      const response = await api.put('/api/auth/change-password', passwordData)

      if (response.data.success) {
        message.success('เปลี่ยนรหัสผ่านสำเร็จ')
        return { success: true }
      } else {
        message.error(response.data.message || 'เปลี่ยนรหัสผ่านไม่สำเร็จ')
        return { success: false, message: response.data.message }
      }
    } catch (error) {
      console.error('Password change error:', error)

      const errorMessage = error.response?.data?.message || 'เกิดข้อผิดพลาดในการเปลี่ยนรหัสผ่าน'
      message.error(errorMessage)

      return { success: false, message: errorMessage }
    }
  }

  // Check permission
  // ✅ แก้ไข hasPermission function
  const hasPermission = (resource, action = 'read') => {

    // console.log('Resource:', resource);
    //console.log('user info:', user);

    // ป้องกัน undefined errors
    if (!user) {
      return false;
    }

    const roleCode = typeof user.role === 'string' ? user.role : user.role?.code
    if (String(roleCode || '').toUpperCase() === 'SUPER_ADMIN') return true

    // ตรวจสอบว่า permissions มีอยู่หรือไม่ก่อน
    const fallbackPermissions = {
      ADMIN: { products:true,contacts:true,quotes:true,receipts:true,expenses:true,expense_import:{read:true,create:true,update:true,confirm:true,retry:true},reports:true,calendar:true,notes:true,settings:true,purchases:true },
      MANAGER: { products:{create:true,read:true,update:true},receipts:{create:true,read:true,update:true},quotes:{create:true,read:true,update:true},expenses:{create:true,read:true,update:true},expense_import:{read:true,create:true,update:true,retry:true} },
      SALE: { receipts:true,products:{read:true,update_stock:true},contacts:{read:true} },
      ACCOUNTING: { receipts:true,expenses:true,expense_import:{read:true,create:true,update:true,confirm:true,retry:true},reports:true,contacts:true,settings:true,accounting:true,products:{read:true} },
      SUPPORT: { calendar:{create:true,read:true,update:true},notes:{create:true,read:true,update:true} }
    }
    const configuredPermissions = user.permissions || {}
    const permissions = Object.keys(configuredPermissions).length ? configuredPermissions : (fallbackPermissions[String(roleCode||'').toUpperCase()] || {})
    if (!permissions) return false  // เพิ่มจุดนี้เพื่อป้องกัน error

    // Debug logging for settings permission
    // if (resource === 'settings') {
    //   //console.log('role', user);
    //   // console.log('Checking settings permission:', {
    //   //   user: user.name,
    //   //   role: user.role,
    //   //   permissions: permissions,
    //   //   hasAll: permissions.all === true
    //   // });
    // }

    // Super admin has all permissions
    if (permissions.all === true) return true

    const aliases = {
      purchases: ['purchases', 'receipts'], delivery_notes: ['delivery_notes', 'quotes'],
      pending_deliveries: ['pending_deliveries', 'quotes'], accounting: ['accounting', 'reports'],
      director_advance: ['director_advance', 'expenses'], bank_statement: ['bank_statement', 'accounting']
    }
    const candidates = aliases[resource] || [resource]
    const legacyAction = { approve:'update', post_accounting:'update', export:'read', manage_settings:'update', approve_negative_stock:'update', retry:'update' }
    for (const key of candidates) {
      const resourcePermission = permissions[key]
      if (resourcePermission === true) return true
      if (typeof resourcePermission === 'object' && (resourcePermission[action] === true || resourcePermission[legacyAction[action]] === true)) return true
    }

    return false;
  };

  // Check role
  const hasRole = (...roles) => {
    if (!user || !user.role) return false
    const roleCode = typeof user.role === 'string' ? user.role : user.role.code
    return roles.includes(roleCode)
  }

  const canAccessPath = (pathname) => {
    if (!pathname || pathname === '/') return true
    if (hasRole('SUPER_ADMIN') && pathname.startsWith('/settings/access-control')) return true
    const candidates = accessMenus.filter(menu => menu.path && (pathname === menu.path || pathname.startsWith(`${menu.path}/`)))
      .sort((a, b) => b.path.length - a.path.length)
    if (!candidates.length) return true
    const menu = candidates[0]
    return Boolean(menu.is_enabled) && (!menu.resource_key || hasPermission(menu.resource_key, 'read'))
  }

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    updateProfile,
    changePassword,
    hasPermission,
    hasRole,
    accessMenus,
    accessLoading,
    refreshAccess,
    canAccessPath,
    isAuthenticated: !!user
  }

  // ✅ ส่วนนี้แหละ ที่คุณถามว่าอยู่ไฟล์ไหน — อยู่ที่นี่เลย!
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
